import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useAuthStore } from "../store/authStore";
import { toast } from "../components/ui/Toaster";

const RegisterPage: React.FC = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    // Set default values for required backend fields
    birthdate: "2000-01-01",
    gender: "LIEVER_NIET" as const,
    socialPreference: "GEMIDDELD" as const,
    drugUsePreference: "FLEXIBEL_OMGEVING" as const,
    musicalPreferences: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [termsAccepted, setTermsAccepted] = useState(false);

  const { register } = useAuth();
  const { isLoading } = useAuthStore();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear field-specific error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.username.trim()) {
      newErrors.username = "Gebruikersnaam is vereist";
    } else if (formData.username.length < 3) {
      newErrors.username = "Gebruikersnaam moet minimaal 3 tekens bevatten";
    } else if (formData.username.length > 20) {
      newErrors.username = "Gebruikersnaam mag maximaal 20 tekens bevatten";
    } else if (!/^[a-zA-Z]/.test(formData.username)) {
      newErrors.username = "Gebruikersnaam moet met een letter beginnen";
    } else if (!/^[a-zA-Z0-9_-]+$/.test(formData.username)) {
      newErrors.username = "Gebruikersnaam mag alleen letters, cijfers, underscores (_) en streepjes (-) bevatten — geen spaties";
    }

    if (!formData.email.trim()) {
      newErrors.email = "E-mail is vereist";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Voer een geldig e-mailadres in";
    }

    if (!formData.password) {
      newErrors.password = "Wachtwoord is vereist";
    } else {
      const passwordErrors = [];
      if (formData.password.length < 8) {
        passwordErrors.push("minstens 8 tekens");
      }
      if (!/[A-Z]/.test(formData.password)) {
        passwordErrors.push("een hoofdletter");
      }
      if (!/[a-z]/.test(formData.password)) {
        passwordErrors.push("een kleine letter");
      }
      if (!/[0-9]/.test(formData.password)) {
        passwordErrors.push("een cijfer");
      }
      if (!/[^A-Za-z0-9]/.test(formData.password)) {
        passwordErrors.push("een speciaal teken");
      }
      
      if (passwordErrors.length > 0) {
        newErrors.password = `Wachtwoord moet bevatten: ${passwordErrors.join(", ")}`;
      }
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Wachtwoorden komen niet overeen";
    }

    if (!termsAccepted) {
      newErrors.terms = "Je moet akkoord gaan met de algemene voorwaarden en het privacybeleid";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await register(formData);
      
      // Store email for verification resend functionality
      localStorage.setItem('pendingVerificationEmail', formData.email);
      
      // Show success message
      toast.success(
        "Registratie succesvol! 📧", 
        "Controleer je e-mail voor de verificatielink voordat je inlogt."
      );
      
      // Navigate to email verification page
      navigate("/verify-email");
    } catch (error: any) {
      console.error("Registration error:", error);

      // useAuth().register rethrows a plain Error whose message is either the
      // backend's (Dutch) validation message(s) or a fixed backend error like
      // "Username is already taken" — map it to the right form field.
      const errorMessage = error.message || "Registratie mislukt. Probeer het opnieuw.";
      const lowered = errorMessage.toLowerCase();

      if (lowered.includes("username is already taken") || lowered.includes("al in gebruik")) {
        setErrors({ username: "Deze gebruikersnaam is al in gebruik" });
      } else if (lowered.includes("email is already registered") || lowered.includes("al geregistreerd")) {
        setErrors({ email: "Dit e-mailadres is al geregistreerd" });
      } else if (lowered.includes("gebruikersnaam")) {
        setErrors({ username: errorMessage });
      } else if (lowered.includes("wachtwoord")) {
        setErrors({ password: errorMessage });
      } else if (lowered.includes("e-mail") || lowered.includes("email")) {
        setErrors({ email: errorMessage });
      } else {
        toast.error("Registratie mislukt", errorMessage);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="sf-eyebrow mb-3">// account aanmaken</div>
        <h2 className="text-2xl font-bold text-fg-bright tracking-tight">
          Maak je account aan
        </h2>
        <p className="mt-2 text-sm text-fg-muted">
          Binnen een minuut geregeld — let's go
        </p>
      </div>

      <div className="">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Username field */}
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-semibold text-fg-soft"
              >
                Gebruikersnaam
              </label>
              <div className="mt-1.5">
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  maxLength={20}
                  value={formData.username}
                  onChange={handleChange}
                  className={`sf-input ${
                    errors.username ? "!border-neon-pink/60" : ""
                  }`}
                  placeholder="Kies een gebruikersnaam"
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-neon-pink-soft">{errors.username}</p>
                )}
              </div>
            </div>

            {/* Email field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-semibold text-fg-soft"
              >
                E-mailadres
              </label>
              <div className="mt-1.5">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`sf-input ${
                    errors.email ? "!border-neon-pink/60" : ""
                  }`}
                  placeholder="je@email.nl"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-neon-pink-soft">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Password field */}
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-fg-soft"
              >
                Wachtwoord
              </label>
              <div className="mt-1.5 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`sf-input pr-10 ${
                    errors.password ? "!border-neon-pink/60" : ""
                  }`}
                  placeholder="Min. 8 tekens, hoofdletter, cijfer, speciaal teken"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-fg-faint" />
                  ) : (
                    <Eye className="h-5 w-5 text-fg-faint" />
                  )}
                </button>
                {errors.password && (
                  <p className="mt-1 text-sm text-neon-pink-soft">{errors.password}</p>
                )}
              </div>
            </div>

            {/* Confirm Password field */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-semibold text-fg-soft"
              >
                Bevestig wachtwoord
              </label>
              <div className="mt-1.5 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`sf-input pr-10 ${
                    errors.confirmPassword ? "!border-neon-pink/60" : ""
                  }`}
                  placeholder="Herhaal je wachtwoord"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-fg-faint" />
                  ) : (
                    <Eye className="h-5 w-5 text-fg-faint" />
                  )}
                </button>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-neon-pink-soft">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>
            </div>

            {/* Terms and conditions checkbox */}
            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => {
                    setTermsAccepted(e.target.checked);
                    if (errors.terms && e.target.checked) {
                      setErrors((prev) => ({ ...prev, terms: "" }));
                    }
                  }}
                  className={`mt-1 mr-3 h-4 w-4 rounded border bg-white/[.04] accent-neon-pink ${
                    errors.terms ? "border-neon-pink/60" : "border-white/20"
                  }`}
                />
                <label htmlFor="terms" className="text-sm text-fg-muted leading-5">
                  Ik ben minimaal 18 jaar oud en ga akkoord met de{" "}
                  <Link
                    to="/algemene-voorwaarden"
                    className="sf-link"
                    target="_blank"
                  >
                    Algemene Voorwaarden
                  </Link>{" "}
                  en het{" "}
                  <Link
                    to="/privacy-statement"
                    className="sf-link"
                    target="_blank"
                  >
                    Privacybeleid
                  </Link>
                </label>
              </div>
              {errors.terms && (
                <p className="text-sm text-neon-pink-soft">{errors.terms}</p>
              )}
            </div>

            {/* Submit button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="sf-btn-primary w-full py-3.5 text-base"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                    Bezig met aanmaken...
                  </>
                ) : (
                  "Account aanmaken"
                )}
              </button>
            </div>

            {/* Login link */}
            <div className="text-sm text-center">
              <span className="text-fg-muted">Heb je al een account? </span>
              <Link
                to="/login"
                className="sf-link"
              >
                Log hier in
              </Link>
            </div>
          </form>
      </div>
    </div>
  );
};

export default RegisterPage;